import { useState } from 'react';
import type { Project } from '../lib/types';

/**
 * COMPANY FIRST, THEN ITS PROJECTS — for booking time.
 *
 * Every company has a "Tasks", a "Bugs" and a "Graphic Design" project, so one list of every
 * project anybody can book against read "TASKS · Tasks" four times over with nothing to say
 * whose was whose. Choosing the company narrows the list to its own projects; "All companies"
 * keeps the whole list, grouped under each company's name so the duplicates are told apart.
 *
 * The company last chosen is remembered in this browser, because the same person books to the
 * same company most days. A person in one company is not asked at all.
 */
const REMEMBERED = 'hub.timeCompany';

function remembered(): string {
  try {
    return localStorage.getItem(REMEMBERED) ?? '';
  } catch {
    return '';
  }
}

interface Props {
  companies: Array<{ id: string; name: string }>;
  projects: Project[];
  value: string;
  onChange: (projectId: string) => void;
  /** What the empty project option says. */
  placeholder?: string;
  minWidth?: number;
}

export function CompanyProjectPicker({ companies, projects, value, onChange, placeholder = 'Choose…', minWidth = 180 }: Props) {
  /* Only companies that have something to book against, in the order the session lists them. */
  const withProjects = companies.filter((company) => projects.some((project) => project.companyId === company.id));
  const [companyId, setCompanyId] = useState(() => {
    const saved = remembered();
    return withProjects.some((company) => company.id === saved) ? saved : '';
  });

  /* The chosen project's company wins, so a project picked elsewhere is never hidden. */
  const selected = projects.find((project) => project.id === value);
  const showing = selected ? selected.companyId : companyId;
  const single = withProjects.length <= 1;

  function chooseCompany(id: string) {
    setCompanyId(id);
    try {
      localStorage.setItem(REMEMBERED, id);
    } catch {
      /* Remembering is a convenience; choosing works without it. */
    }
    if (selected && id && selected.companyId !== id) onChange('');
  }

  const option = (project: Project) => (
    <option key={project.id} value={project.id}>
      {project.name}
      {project.code ? ` · ${project.code}` : ''}
    </option>
  );

  return (
    <>
      {!single && (
        <label className="field" style={{ marginBottom: 0, minWidth: 160 }}>
          <span className="field-label">Company</span>
          <select value={showing} onChange={(e) => chooseCompany(e.target.value)}>
            <option value="">All companies</option>
            {withProjects.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="field" style={{ marginBottom: 0, minWidth }}>
        <span className="field-label">Project</span>
        <select value={value} onChange={(e) => onChange(e.target.value)} required>
          <option value="">{placeholder}</option>
          {single || showing
            ? projects.filter((project) => single || project.companyId === showing).map(option)
            : withProjects.map((company) => (
                <optgroup key={company.id} label={company.name}>
                  {projects.filter((project) => project.companyId === company.id).map(option)}
                </optgroup>
              ))}
        </select>
      </label>
    </>
  );
}
